/**
 * Max. flight altitude of the mission form with the recommendation of the ŘLP height grid under the
 * flight area (lib/heightLimit.js): where the value comes from, a warning over the grid height, the
 * "use N m" link, and the cases without a value to fill in (0 m cell, outside the grid, an error).
 * Never blocks saving; the 1–500 m range is checked on submit.
 */
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { fillTemplate } from "../../lib/arcDisplay";
import { ALTITUDE_HINT } from "../../lib/heightLimit";

const HintLine = ({ className, testId, children }) => (
  <p className={`text-xs ${className || ""}`} data-testid={testId}>{children}</p>
);

export const MissionAltitudeField = ({ value, onChange, hint, onApply, t = {} }) => {
  const texts = t.flightOps?.altitudeGrid || {};
  const muted = { color: 'var(--text-muted)' };
  const limit = hint?.limit;
  const kind = hint?.kind || ALTITUDE_HINT.DEFAULT;
  const withValue = (template) => fillTemplate(template, { value: limit, cells: hint?.cells });
  const isRecommendation = [ALTITUDE_HINT.AUTO, ALTITUDE_HINT.MATCHES, ALTITUDE_HINT.MANUAL, ALTITUDE_HINT.STORED,
    ALTITUDE_HINT.DIFFERS].includes(kind);

  const sourceText = {
    [ALTITUDE_HINT.AUTO]: withValue(hint?.cells > 1 ? texts.auto : texts.autoSingle),
    [ALTITUDE_HINT.MATCHES]: withValue(texts.matches),
    [ALTITUDE_HINT.MANUAL]: withValue(texts.manual),
    [ALTITUDE_HINT.STORED]: withValue(texts.stored),
    [ALTITUDE_HINT.DIFFERS]: withValue(texts.differs),
  }[kind];

  return (
    <div className="space-y-2">
      <Label className="text-slate-600">{t.flightOps?.maxAltitude || "Max. flight altitude (m AGL)"}</Label>
      {/* Range checked in handleSubmit, not by native min/max: the browser bubble ignores the app language. */}
      <Input
        type="number"
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        data-testid="mission-max-altitude"
        className="bg-white border-slate-300 text-slate-900"
      />
      {kind === ALTITUDE_HINT.DEFAULT && (
        <HintLine testId="mission-altitude-hint-default"><span style={muted}>{t.flightOps?.maxAltitudeHelper || "Maximum planned altitude above ground level"}</span></HintLine>
      )}
      {kind === ALTITUDE_HINT.LOADING && (
        <HintLine testId="mission-altitude-hint-loading"><span style={muted}>{texts.loading}</span></HintLine>
      )}
      {kind === ALTITUDE_HINT.ERROR && (
        <HintLine className="text-slate-500" testId="mission-altitude-hint-error">{texts.error}</HintLine>
      )}
      {kind === ALTITUDE_HINT.OUTSIDE && (
        <HintLine testId="mission-altitude-hint-outside"><span style={muted}>{texts.outside}</span></HintLine>
      )}
      {kind === ALTITUDE_HINT.ZERO && (
        <div className="rounded-md border border-red-300 bg-red-50 p-2" data-testid="mission-altitude-hint-zero">
          <p className="text-xs font-medium text-red-700">{texts.zero}</p>
        </div>
      )}
      {isRecommendation && (
        <div className="space-y-1" data-testid={`mission-altitude-hint-${kind}`}>
          <HintLine className="text-slate-700">{sourceText}</HintLine>
          {hint.above && (
            <HintLine className="text-amber-700" testId="mission-altitude-hint-above">{withValue(texts.above)}</HintLine>
          )}
          {hint.partial && <HintLine className="text-slate-600">{texts.partial}</HintLine>}
          {hint.canApply && (
            <button
              type="button"
              className="text-xs font-medium text-blue-700 underline hover:text-blue-900"
              onClick={onApply}
              data-testid="mission-altitude-apply"
            >
              {withValue(texts.apply)}
            </button>
          )}
          <HintLine><span style={muted}>{texts.disclaimer}</span></HintLine>
        </div>
      )}
    </div>
  );
};
